import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Button,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { getStudentFeeRecords } from "../../redux/feeRelated/feeHandle";
import StudentFeePaymentDialog from "../../components/student/StudentFeePaymentDialog";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { calculateOverallAttendancePercentage } from "../../components/attendanceCalculator";
import CustomPieChart from "../../components/CustomPieChart";
import { getUserDetails } from "../../redux/userRelated/userHandle";
import SeeNotice from "../../components/SeeNotice";
import { getSubjectList } from "../../redux/sclassRelated/sclassHandle";
import { FORM3_MIN_SUBJECTS } from "../../utils/form3SubjectSelection";
import { useStudentSubjectSelectionStatus } from "../../hooks/useStudentSubjectSelectionStatus";
import { getStudentClassId, getStudentClassLabel } from "../../utils/userContext";
import { resetSubjects } from "../../redux/sclassRelated/sclassSlice";
import {
    StudentPageContainer,
    StudentPageHeader,
    StudentStatCard,
    StudentPageLoading,
} from "./studentPortalUi";

const StudentHomePage = () => {
    const dispatch = useDispatch();
    const { userDetails, currentUser, loading } = useSelector((state) => state.user);
    const { subjectsList } = useSelector((state) => state.sclass);
    const [subjectAttendance, setSubjectAttendance] = useState([]);
    const studentId = currentUser?._id || currentUser?.id;
    const classID = getStudentClassId(currentUser, userDetails);
    const classLabel = getStudentClassLabel(currentUser, userDetails);
    const studentName = currentUser?.name?.split(" ")[0] || "Student";
    const { studentFeeRecords } = useSelector((state) => state.fee);
    const [payOpen, setPayOpen] = useState(false);

    useEffect(() => {
        if (studentId) dispatch(getStudentFeeRecords(studentId));
    }, [dispatch, studentId]);

    const feeSummary = studentFeeRecords?.summary || { totalBalance: 0 };
    const feeBalance = Math.round(Number(feeSummary.totalBalance) || 0);
    const paymentSettings = studentFeeRecords?.paymentSettings;
    const feeRecords = Array.isArray(studentFeeRecords?.records) ? studentFeeRecords.records : [];
    const pendingSubmissions = (studentFeeRecords?.submissions || []).filter((s) => s.status === "Pending");

    const { status, needsSelection } = useStudentSubjectSelectionStatus();

    useEffect(() => {
        dispatch(resetSubjects());
    }, [dispatch]);

    useEffect(() => {
        if (!currentUser?._id) return;
        dispatch(getUserDetails(currentUser._id, "Student"));
        if (classID && !needsSelection) dispatch(getSubjectList(classID, "ClassSubjects"));
    }, [dispatch, currentUser?._id, classID, needsSelection]);

    useEffect(() => {
        if (userDetails) setSubjectAttendance(userDetails.attendance || []);
    }, [userDetails]);

    const selectionBanner = needsSelection
        ? {
              type: "pick",
              level: status?.formLevel === "Form4" ? "Form 4" : "Form 3",
              min: status?.minSubjects || FORM3_MIN_SUBJECTS,
          }
        : status?.carriedForward
          ? {
                type: "form4",
                count: status?.chosenSubjectIds?.length || status?.subjects?.length || 0,
            }
          : null;

    const numberOfSubjects = needsSelection
        ? 0
        : status?.chosenSubjectIds?.length >= FORM3_MIN_SUBJECTS
          ? status.chosenSubjectIds.length
          : subjectsList?.length ?? 0;
    const marksCount = userDetails?.examResult?.filter((r) => r?.subName && r.marksObtained != null)?.length ?? 0;

    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = useMemo(
        () => [
            { name: "Present", value: overallAttendancePercentage },
            { name: "Absent", value: overallAbsentPercentage },
        ],
        [overallAttendancePercentage, overallAbsentPercentage],
    );

    const hasAttendance = Array.isArray(subjectAttendance) && subjectAttendance.length > 0;

    if (loading && !userDetails) {
        return <StudentPageLoading />;
    }

    return (
        <StudentPageContainer maxWidth="lg">
            {selectionBanner?.type === "pick" ? (
                <Alert
                    severity="warning"
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            component={RouterLink}
                            to="/Student/form3-subjects"
                            sx={{ fontWeight: 700 }}
                        >
                            Choose subjects
                        </Button>
                    }
                >
                    Complete your {selectionBanner.level} subject line — pick at least {selectionBanner.min}{" "}
                    subjects to finish enrollment.
                </Alert>
            ) : null}
            {selectionBanner?.type === "form4" ? (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    You are continuing in Form 4 with your{" "}
                    <strong>{selectionBanner.count || "selected"}</strong> subject
                    {selectionBanner.count === 1 ? "" : "s"} from Form 3. View them under{" "}
                    <strong>Subjects</strong> in the menu.
                </Alert>
            ) : null}
            <StudentPageHeader
                eyebrow="Student portal"
                title={`Welcome, ${studentName}`}
                subtitle={
                    classLabel
                        ? `Class ${classLabel} · ${currentUser?.school?.schoolName || "Your school"}`
                        : "Your dashboard for marks, attendance, and school notices."
                }
                action={
                    <Button
                        component={RouterLink}
                        to="/Student/results"
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ fontWeight: 700, alignSelf: { xs: "stretch", sm: "center" } }}
                    >
                        View results
                    </Button>
                }
            />

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StudentStatCard
                        title="Fee balance"
                        value={feeBalance}
                        hint={
                            pendingSubmissions.length
                                ? `${pendingSubmissions.length} payment(s) awaiting verification`
                                : feeBalance > 0
                                  ? "Outstanding on your account"
                                  : "Up to date"
                        }
                        icon={feeBalance > 0 ? AccountBalanceWalletOutlinedIcon : PaymentsOutlinedIcon}
                        color={feeBalance > 0 ? "warning" : "success"}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StudentStatCard
                        title="Subjects"
                        value={numberOfSubjects}
                        hint="In your class"
                        icon={MenuBookOutlinedIcon}
                        color="primary"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StudentStatCard
                        title="Marks recorded"
                        value={marksCount}
                        hint="Subjects with results"
                        icon={GradingOutlinedIcon}
                        color="secondary"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StudentStatCard
                        title="Attendance"
                        value={hasAttendance ? Math.round(overallAttendancePercentage) : 0}
                        hint={hasAttendance ? "% overall present" : "No records yet"}
                        icon={EventAvailableOutlinedIcon}
                        color="success"
                    />
                </Grid>
            </Grid>

            {feeBalance > 0 || pendingSubmissions.length ? (
                <Alert
                    severity={pendingSubmissions.length ? "info" : "warning"}
                    sx={{ mb: 3, borderRadius: 2 }}
                    action={
                        feeBalance > 0 ? (
                            <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={() => setPayOpen(true)}>
                                Make payment
                            </Button>
                        ) : null
                    }
                >
                    {pendingSubmissions.length ? (
                        <>
                            You have <strong>{pendingSubmissions.length}</strong> fee payment
                            {pendingSubmissions.length === 1 ? "" : "s"} waiting for school verification.
                        </>
                    ) : (
                        <>
                            Your outstanding fee balance is <strong>MWK {feeBalance.toLocaleString()}</strong>. Pay using
                            your school&apos;s bank details and upload proof for verification.
                        </>
                    )}
                </Alert>
            ) : null}

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={5}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            height: "100%",
                            minHeight: 280,
                            borderRadius: 2.5,
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, alignSelf: "flex-start" }}>
                            Attendance overview
                        </Typography>
                        {hasAttendance ? (
                            <CustomPieChart data={chartData} />
                        ) : (
                            <Typography color="text.secondary" textAlign="center">
                                No attendance recorded yet. Check back after your teachers submit attendance.
                            </Typography>
                        )}
                        <Button
                            component={RouterLink}
                            to="/Student/attendance"
                            size="small"
                            sx={{ mt: 2, fontWeight: 600 }}
                        >
                            Full attendance
                        </Button>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                School notices
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button component={RouterLink} to="/Student/subjects" size="small">
                                    Subjects
                                </Button>
                                <Button component={RouterLink} to="/Student/resources" size="small">
                                    Resources
                                </Button>
                                <Button component={RouterLink} to="/Student/fees" size="small">
                                    Fees
                                </Button>
                            </Stack>
                        </Stack>
                        <SeeNotice />
                    </Paper>
                </Grid>
            </Grid>

            <StudentFeePaymentDialog
                open={payOpen}
                onClose={() => setPayOpen(false)}
                studentId={studentId}
                records={feeRecords}
                paymentSettings={paymentSettings}
                onSubmitted={() => studentId && dispatch(getStudentFeeRecords(studentId))}
            />
        </StudentPageContainer>
    );
};

export default StudentHomePage;
